import React from 'react';
import { keys, first, includes, map, reject, size, compact } from 'lodash';
import styled from 'styled-components';
import {
    addTilesToDeckFromHand,
    addTilesToHandFromDeck,
    hydrateState,
    incrementCommandsRun,
} from '../helpers/gameReducer';

import { peel, split, getTiles, dump } from '../helpers/gameServer';
import { Hand } from './Hand';
import { allTiles } from '../helpers/tiles';
import { SelectedTiles } from '../components/SelectedTiles';
import { useParams, Link, useHistory } from 'react-router-dom';
import { useCommands, useSubmittedBoards } from '../hooks';
import { getStateFromStorage } from '../helpers/storage';
import { BoardState } from '../containers/BoardState';
import { useGameCallbacks } from '../hooks/gameCallbacks';
import { BoardTransformer } from './BoardTransformer';
import { GameProviders } from './GameProviders';
import { MagnifierContainer } from '../containers/MagnifierContainer';
import { UserId } from '../containers/UserId';
import { User } from '../types';
import { HandSizes } from './HandSizes';

const HandSizesContainer = styled.div`
    position: relative;
`;
const StyledHandSizes = styled(HandSizes)`
    position: absolute;
    bottom: 0;
`;

const Header = styled.div`
    position: absolute;
    top: 0;
    width: 100%;
    display: flex;
    z-index: 2;
    padding: 10px;
    justify-content: space-between;
`;

const GameContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    * {
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }
    .react-transform-component {
        width: auto;
        height: auto;
        flex: 1;
    }
`;

const BoardContainer = styled.div`
    display: flex;
    min-height: 0;
    min-width: 0;
    flex: 1;
    position: relative;
`;

// Prevents panning when
// safari activates previous page
const TransformShim = styled.div`
    position: absolute;
    height: 100%;
    left: 0;
    top: 0;
    width: 35px;
    // border: 1px solid black;
    z-index: 1;
`;

type GameProps = {
    users: User[],
};

const MagnifierToggle = () => {
    const { magnifier, setMagnifier } = MagnifierContainer.useContainer();
    return (
        <div>
            <input
                onChange={(e) => setMagnifier(e.target.checked)}
                type="checkbox"
                id="scales"
                name="scales"
                checked={magnifier}
            />
            <label htmlFor="scales">Magnifier</label>
        </div>
    );
};

export const Game: React.FC<GameProps> = ({ users }) => {
    const { id: roomId } = useParams();
    const history = useHistory();
    const { submittedBoards } = useSubmittedBoards(roomId);
    const submittingUser = first(keys(submittedBoards));

    React.useEffect(() => {
        if (submittingUser) {
            history.push(`/game/${roomId}/review/${submittingUser}`);
        }
    }, [history, submittingUser, roomId]);

    return (
        <GameProviders key="game">
            <GameWithoutProviders users={users} />
        </GameProviders>
    );
};

const GameWithoutProviders: React.FC<GameProps> = ({ users }) => {
    const userIds = users.map(({ userId }) => userId);
    const { userId } = UserId.useContainer();
    const { id: roomId } = useParams();

    const [tilesLeft, setTilesLeft] = React.useState<number>(size(allTiles));

    const { state, dispatch } = BoardState.useContainer();

    const { hand, commandsRun } = state;

    const {
        handlePeel,
        handleDump,
        handleDropOnHand,
        handleSubmit,
        handleClickTile,
        handleClickSelectedTile,
    } = useGameCallbacks({ userId, roomId });

    const { commands } = useCommands(roomId);

    const commandsToRun = React.useMemo(
        () => reject(commands, (command) => includes(commandsRun, command.id)),
        [commands, commandsRun],
    );

    React.useEffect(() => {
        if (!!size(commandsToRun)) {
            // Only triggeres a rerender if things actually changed
            dispatch(incrementCommandsRun(map(commandsToRun, ({ id }) => id)));
            // Proceed to run the commands
            commandsToRun.forEach(({ command, id }) => {
                if (command.action === 'split') {
                    const hand = split(userIds, command.seed)[userId];
                    if (hand) {
                        dispatch(addTilesToHandFromDeck(map(hand, ({ id }) => id)));
                    }
                } else if (command.action === 'peel') {
                    const newTile = peel({ userId: command.userId, userIds, seed: command.seed })[
                        userId
                    ];
                    if (newTile) {
                        dispatch(addTilesToHandFromDeck(map([newTile], ({ id }) => id)));
                    }
                    // dump
                } else {
                    const newTiles = dump({
                        userId: command.userId,
                        tileToDump: command.tileToDump,
                        seed: command.seed,
                    });
                    const tilesForUser = newTiles[userId];
                    if (tilesForUser) {
                        dispatch(addTilesToDeckFromHand([command.tileToDump]));
                        dispatch(addTilesToHandFromDeck(map(tilesForUser, ({ id }) => id)));
                    }
                }
                // Restore hand state
                const storedState = getStateFromStorage()?.[roomId];
                if (storedState?.id === id) {
                    console.log('Restoring state', storedState.state);
                    dispatch(hydrateState(storedState.state));
                }
                setTilesLeft(size(getTiles()));
            });
        }
    }, [dispatch, roomId, commandsToRun, userId, userIds]);

    return (
        <GameContainer>
            <Header>
                <Link to="/">Home</Link>
                <div>Tiles left: {tilesLeft}</div>
                <MagnifierToggle />
            </Header>
            <BoardContainer>
                <BoardTransformer />
            </BoardContainer>
            <SelectedTiles onClick={handleClickSelectedTile} />
            <HandSizesContainer>
                <StyledHandSizes users={users} roomId={roomId} />
            </HandSizesContainer>
            <Hand
                onClickTile={handleClickTile}
                hand={hand}
                onDrop={handleDropOnHand}
                onPeel={tilesLeft ? handlePeel : undefined}
                onDump={tilesLeft ? handleDump : undefined}
                onSubmit={!tilesLeft ? handleSubmit : undefined}
            />
        </GameContainer>
    );
};

